// 9.15 A person's step on a firm's Connect path (docs/specs/CONNECT.md 6.5): the path_step task, and a prepare_quote
// task tied to a path (its "quote" block), with a link to make or open the firm's quote. The card says what to do and,
// for a step a later phase automates, that Ogma will do it then; Done (complete_path_block, with an optional note) closes the task and moves the firm's path on at
// once, instead of waiting for the deal agent's next run. A quote task with no path keeps the plain card. At Qualify the
// person says whether they are a fit (migration 20270221090000), for the step's routes.
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { BLOCKS, type BlockKey, type PathOutcome } from "../../lib/paths";
import { SELLING_LABEL, type SellingMethod } from "../../lib/connect";
import { KeyValues, Notice } from "../../components/ui";
import { PathSteps, runSteps } from "../../components/PathSteps";
import { OtherTask } from "./TaskCards";

/** What the card needs from a path task's payload (engine/paths.py step_task_payload, quote_task). */
export function pathTaskInfo(task: Pick<HumanTask, "kind" | "payload" | "entity_type" | "entity_id">): {
  run: string | null;
  block: BlockKey | null;
  blockLabel: string;
  organisation: string;
  organisationId: string | null;
  pathLabel: string | null;
  how: string;
  later: string | null;
} {
  const p = task.payload ?? {};
  const block = typeof p.block === "string" && p.block in BLOCKS ? (p.block as BlockKey) : task.kind === "prepare_quote" && p.run_id ? "quote" : null;
  const info = block ? BLOCKS[block] : null;
  const later = typeof p.automated_in === "string" ? p.automated_in : (info?.automated_in ?? null);
  return {
    run: typeof p.run_id === "string" ? p.run_id : null,
    block,
    blockLabel: typeof p.block_label === "string" ? p.block_label : (info?.label ?? "This step"),
    organisation: typeof p.organisation === "string" ? p.organisation : "this firm",
    organisationId: typeof p.organisation_id === "string" ? p.organisation_id : task.entity_type === "organisation" ? task.entity_id : null,
    pathLabel: typeof p.path_label === "string" ? p.path_label : null,
    how: typeof p.how === "string" && p.how.trim() ? p.how : (info?.what ?? "Do this step, then press Done."),
    later: info?.kind === "person" ? later : null,
  };
}

/** The line in the queue. */
export function pathStepSummary(task: HumanTask): string {
  const i = pathTaskInfo(task);
  return `${i.blockLabel} for ${i.organisation}${i.pathLabel ? ` (${i.pathLabel})` : ""}`;
}

/** A quote task's line: the selling method's words, with its path when it has one. */
export function quoteSummary(task: HumanTask): string {
  const p = task.payload ?? {};
  const base = `${SELLING_LABEL[p.method as SellingMethod] ?? "Quote"} for ${String(p.organisation ?? "a firm")}`;
  return p.run_id && typeof p.path_label === "string" ? `${base} (${p.path_label})` : base;
}

export function PathStepTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const i = pathTaskInfo(task);
  const run = useAsync(() => (i.organisationId ? crm.connectRun({ organisation: i.organisationId }).catch(() => null) : Promise.resolve(null)), [crm, i.organisationId]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  if (!i.run || !i.block) return <OtherTask crm={crm} task={task} onDone={onDone} />;
  const r = run.data && run.data.run_id === i.run ? run.data : null;

  const done = async (result?: PathOutcome) => {
    setBusy(true);
    setError(null);
    try {
      const res = await crm.completePathBlock(i.run!, i.block!, note, result);
      // The step had already moved on (Ogma saw it happen, or someone else pressed Done): close this task as done too.
      if (!res?.done) await crm.completeTask(task.id, "done", { note: note.trim() || res?.reason || "step already passed" });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="card-lede">
        <strong>{i.blockLabel}</strong> for{" "}
        {i.organisationId ? <Link to={`/contacts/companies/${i.organisationId}`}>{i.organisation}</Link> : i.organisation}
        {i.pathLabel ? <span className="muted"> on the path {i.pathLabel}</span> : null}
      </p>
      <p>{i.how}</p>
      {i.block === "quote" && i.organisationId && (
        // The Quote step is done when the quote goes out (the quotes_sent_moves_path trigger closes this task with it).
        <p>
          <Link className="btn btn-small" to={`/contacts/companies/${i.organisationId}#make-quote`}>
            Make or open their quote
          </Link>{" "}
          <span className="muted small">This step is done, and this task closes, when the quote is sent.</span>
        </p>
      )}
      {i.later && (
        <p className="muted small">
          <span className="chip chip-warn">You, for now</span> Ogma will do this step itself from {i.later} of Connect.
        </p>
      )}
      {task.kind === "prepare_quote" && typeof task.payload.method === "string" && <KeyValues rows={[["How you sell", SELLING_LABEL[task.payload.method as SellingMethod] ?? String(task.payload.method)]]} />}
      {r && <PathSteps label={`Steps of ${r.path_label}`} steps={runSteps(r, (k) => BLOCKS[k]?.automated_in ?? null)} />}
      <label className="field" htmlFor={id}>
        <span className="field-label">Note (optional)</span>
        <textarea id={id} rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        {i.block === "qualify" ? (
          // Qualify's outcome: the path's routes from Qualify can go one way for a fit and another for not a fit.
          <>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void done("fit")}>
              {busy ? "Saving…" : "Done: a fit"}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void done("not_fit")}>
              Done: not a fit
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void done()}>
            {busy ? "Saving…" : "Done"}
          </button>
        )}
      </div>
    </div>
  );
}

/** The line in the queue for a route a person chooses (9.25). */
export function chooseRouteSummary(task: HumanTask): string {
  const i = pathTaskInfo(task);
  return `Where ${i.organisation} goes after ${i.blockLabel}`;
}

/**
 * 9.25 A route on the firm's path says a person chooses where it goes next (the choose_route task): one button per
 * place it can go (crm__route_choices: a later step, yes, lost, ReConnect). Choosing moves the path on at once.
 */
export function ChooseRouteTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const i = pathTaskInfo(task);
  const options = (Array.isArray(task.payload.options) ? task.payload.options : []).flatMap((o) =>
    o && typeof o === "object" && !Array.isArray(o) && typeof o.to === "string" && typeof o.label === "string" ? [{ to: o.to, label: o.label }] : [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!i.run) return <OtherTask crm={crm} task={task} onDone={onDone} />;
  const choose = async (to: string) => {
    setBusy(true);
    setError(null);
    try {
      await crm.chooseRoute(i.run!, to);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="card-lede">
        <strong>{i.blockLabel}</strong> is done for{" "}
        {i.organisationId ? <Link to={`/contacts/companies/${i.organisationId}`}>{i.organisation}</Link> : i.organisation}
        {i.pathLabel ? <span className="muted"> on the path {i.pathLabel}</span> : null}. Where do they go next?
      </p>
      <p className="inline wrap" role="group" aria-label="Where they go next">
        {options.map((o) => (
          <button key={o.to} type="button" className="btn btn-small" disabled={busy} onClick={() => void choose(o.to)}>
            {o.label}
          </button>
        ))}
      </p>
      <Notice text={error} tone="bad" />
    </div>
  );
}
